// Utilidades compartidas del módulo Producción (BOM, tarifas, órdenes de trabajo).
// CRUD directo a PostgREST vía /rest/produccion.<tabla> + RPCs del esquema produccion.
import type { QueryClient } from '@tanstack/react-query';
import { apiClient } from '@/lib/api/client';

const base = (t: string) => `/rest/produccion.${t}`;
const repr = { headers: { Prefer: 'return=representation' } };

export const prod = {
  list: async <T,>(t: string, query = ''): Promise<T[]> =>
    (await apiClient.get<T[]>(`${base(t)}${query}`)).data,
  create: async (t: string, body: unknown) =>
    (await apiClient.post(base(t), body, repr)).data,
  update: async (t: string, id: string, body: unknown) =>
    (await apiClient.patch(`${base(t)}?id=eq.${id}`, body, repr)).data,
  remove: async (t: string, id: string) =>
    (await apiClient.delete(`${base(t)}?id=eq.${id}`)).data,
};

export const PROD = { headers: { 'Content-Profile': 'produccion', 'Accept-Profile': 'produccion' } };

export const prodRpc = async <T,>(fn: string, body: Record<string, unknown> = {}): Promise<T> =>
  (await apiClient.post<T>(`/rpc/${fn}`, body, PROD)).data;

// Invalida producción + inventario (las OT mueven stock/kardex/valorización)
export const invalidateProduccion = (qc: QueryClient): void => {
  void qc.invalidateQueries({
    predicate: (q) =>
      typeof q.queryKey[0] === 'string' &&
      ((q.queryKey[0] as string).startsWith('prod-') || (q.queryKey[0] as string).startsWith('inv-')),
  });
};

// ---- Tipos ----
export interface Bom {
  id: string; producto_id: string; nombre: string | null; version: number; es_activo: boolean;
}
export interface BomLinea {
  id: string; bom_id: string; material_id: string; cantidad: number; escala_talla: boolean; notas: string | null;
}
export interface TallaFactor { id: string; valor_id: string; factor: number }
export interface ConfigCosto {
  id: string; categoria_id: string | null; producto_id: string | null;
  mo_unitario: number; cif_unitario: number; es_activo: boolean;
}
export interface OtResumen {
  id: string; codigo: string; tipo: string; estado: string; fecha: string;
  producto_padre: string; maquilador: string | null; bodega_mp: string; bodega_pt: string;
  unidades_plan: number; unidades_producidas: number;
  costo_mp: number; costo_mo: number; costo_cif: number; costo_maquila: number; costo_merma: number;
}
export interface OtProducto {
  producto_id: string; codigo: string; nombre: string;
  cantidad_plan: number; cantidad_producida: number; mo_unit: number; cif_unit: number; mp_unit: number;
}
export interface OtConsumo {
  material_id: string; codigo: string; nombre: string; unidad: string | null;
  cantidad_plan: number; cantidad_consumida: number; costo_unit: number;
}
export interface OtEntrada { fecha: string; producto: string; cantidad: number; costo_unit: number; ubicacion: string | null }
export interface OtCosto { tipo: string; fecha: string; monto: number; referencia: string | null }
export interface OtDetalle extends Omit<OtResumen, 'producto_padre' | 'maquilador' | 'bodega_mp' | 'bodega_pt'> {
  producto_padre_id: string; producto_padre: string;
  bodega_mp_id: string; bodega_pt_id: string;
  maquilador_id: string | null; maquilador: string | null; observaciones: string | null;
  productos: OtProducto[]; consumos: OtConsumo[]; entradas: OtEntrada[]; costos: OtCosto[];
}

export const ESTADO_LABEL: Record<string, string> = {
  borrador: 'Borrador', en_proceso: 'En proceso', terminada: 'Terminada', cerrada: 'Cerrada', anulada: 'Anulada',
};
export const ESTADO_CLS: Record<string, string> = {
  borrador: 'bg-muted text-muted-foreground',
  en_proceso: 'bg-blue-100 text-blue-800 dark:bg-blue-900/40 dark:text-blue-300',
  terminada: 'bg-amber-100 text-amber-800 dark:bg-amber-900/40 dark:text-amber-300',
  cerrada: 'bg-green-100 text-green-800 dark:bg-green-900/40 dark:text-green-300',
  anulada: 'bg-red-100 text-red-800 dark:bg-red-900/40 dark:text-red-300',
};
